import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type Ad = { id: string; title: string; body: string | null; image_url: string; link_url: string | null; is_active: boolean; created_at: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HTTPS = /^https:\/\/\S+$/;

type Subscriber = { id: string; userId: string; name: string; email: string; months: number; amount: number; currency: string; startedAt: string; expiresAt: string; source: "paid" | "granted" };
type PlusOverview = { prices: Record<string, number>; currency: string; rewindPaywall?: boolean; rewindPaywallUpdatedAt?: string | null; subscribers: Subscriber[]; totals: { active: number; paid: number; granted: number; revenue: number } };

export function PlusPage() {
  const queryClient = useQueryClient();

  // Overview: prices, active subscribers, totals
  const overview = useQuery({
    queryKey: ["plus-overview"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/subscriptions`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load Deuxda Plus");
      return (await response.json()) as PlusOverview;
    },
  });

  // Prices: one per period, editable
  const [prices, setPrices] = React.useState({ "3": "", "6": "", "12": "" });
  React.useEffect(() => {
    if (overview.data) setPrices({ "3": String(overview.data.prices["3"]), "6": String(overview.data.prices["6"]), "12": String(overview.data.prices["12"]) });
  }, [overview.data]);
  const pricesValid = (["3", "6", "12"] as const).every((k) => /^\d+$/.test(prices[k]) && Number(prices[k]) >= 1);
  const savePrices = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/subscriptions/prices`, {
        method: "PUT",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ "3": Number(prices["3"]), "6": Number(prices["6"]), "12": Number(prices["12"]) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Could not save the prices");
      return payload;
    },
    onSuccess: () => {
      toast.success("Prices saved. The app shows them from now on.");
      queryClient.invalidateQueries({ queryKey: ["plus-overview"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Payment page when a Free user scrolls back in the feed (only this feature)
  const rewindPaywall = overview.data?.rewindPaywall ?? true;
  const saveRewindPaywall = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/subscriptions/rewind-paywall`, {
        method: "PUT",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Could not save the setting");
      return payload as { rewindPaywall: boolean };
    },
    onSuccess: (payload) => {
      toast.success(
        payload.rewindPaywall
          ? "Payment page on. Free users must subscribe to scroll back in the feed."
          : "Payment page off. Free users can scroll back in the feed freely.",
      );
      queryClient.invalidateQueries({ queryKey: ["plus-overview"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Grant Deuxda Plus
  const [userId, setUserId] = React.useState("");
  const [months, setMonths] = React.useState("3");
  const validUser = UUID.test(userId.trim());
  const grant = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/subscriptions`, {
        method: "POST",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ userId: userId.trim(), months: Number(months) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Could not grant Deuxda Plus");
      return payload;
    },
    onSuccess: () => toast.success("Deuxda Plus granted for the chosen period."),
    onError: (error: Error) => toast.error(error.message),
  });

  // Ads
  const ads = useQuery({
    queryKey: ["ads"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/ads`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load ads");
      return ((await response.json()) as { ads: Ad[] }).ads;
    },
  });
  const [form, setForm] = React.useState({ title: "", body: "", imageUrl: "", linkUrl: "" });
  const adValid = form.title.trim().length >= 2 && HTTPS.test(form.imageUrl.trim()) && (!form.linkUrl || HTTPS.test(form.linkUrl.trim()));
  const createAd = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/ads`, {
        method: "POST",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ title: form.title.trim(), body: form.body.trim(), imageUrl: form.imageUrl.trim(), linkUrl: form.linkUrl.trim() || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Could not create the ad");
      return payload;
    },
    onSuccess: () => {
      toast.success("Ad created. Free users see it in the feed.");
      setForm({ title: "", body: "", imageUrl: "", linkUrl: "" });
      queryClient.invalidateQueries({ queryKey: ["ads"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const toggleAd = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/ads/${id}`, {
        method: "PATCH",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ isActive }),
      });
      if (!response.ok) throw new Error("Could not update the ad");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ads"] }),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Deuxda Plus & ads" description="Prices, people with Deuxda Plus, granting Plus, and the ads shown to Free users." />

      <Card className="rounded-2xl">
        <CardContent className="space-y-6 p-6">
          <p className="text-sm font-semibold">Prices (CDF, total for the period)</p>
          <div className="grid gap-4 md:grid-cols-3">
            {(["3", "6", "12"] as const).map((k) => (
              <div key={k} className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">{k} months</Label>
                <Input inputMode="numeric" value={prices[k]} onChange={(e) => setPrices({ ...prices, [k]: e.target.value.trim() })} />
              </div>
            ))}
          </div>
          <Button className="rounded-xl" disabled={!pricesValid || savePrices.isPending} onClick={() => savePrices.mutate()}>
            {savePrices.isPending ? "Saving…" : "Save prices"}
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="space-y-4 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold">Payment page for unlimited scroll-back</p>
              <p className="text-sm text-muted-foreground">
                When on, a Free user who scrolls back to an earlier post in the feed sees the Deuxda Plus payment page.
                When off, Free users can scroll back freely.
              </p>
            </div>
            {overview.isLoading ? (
              <Skeleton className="h-5 w-9" />
            ) : (
              <Switch
                aria-label="Payment page for unlimited scroll-back"
                checked={rewindPaywall}
                disabled={!overview.data || saveRewindPaywall.isPending}
                onCheckedChange={(checked) => saveRewindPaywall.mutate(checked)}
              />
            )}
          </div>
          <div className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
            {overview.data ? (rewindPaywall ? "Currently on." : "Currently off.") : ""} This only changes scrolling back. Ads, deleting messages,
            invisible mode and the app PIN lock stay Deuxda Plus only, and prices and subscriptions are not changed.
            {overview.data?.rewindPaywallUpdatedAt && ` Last changed ${new Date(overview.data.rewindPaywallUpdatedAt).toLocaleString("en-GB")}.`}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="space-y-5 p-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-xl bg-muted/40 p-4"><p className="text-xs text-muted-foreground">Active</p><p className="text-2xl font-semibold">{overview.data?.totals.active ?? "—"}</p></div>
            <div className="rounded-xl bg-muted/40 p-4"><p className="text-xs text-muted-foreground">Paid</p><p className="text-2xl font-semibold">{overview.data?.totals.paid ?? "—"}</p></div>
            <div className="rounded-xl bg-muted/40 p-4"><p className="text-xs text-muted-foreground">Granted</p><p className="text-2xl font-semibold">{overview.data?.totals.granted ?? "—"}</p></div>
            <div className="rounded-xl bg-muted/40 p-4"><p className="text-xs text-muted-foreground">Paid revenue</p><p className="text-2xl font-semibold">{overview.data ? `${overview.data.totals.revenue.toLocaleString("en-GB")} CDF` : "—"}</p></div>
          </div>
          {overview.isLoading && <Skeleton className="h-6 w-full" />}
          {overview.isError && <p className="text-sm text-destructive">Deuxda Plus could not be loaded.</p>}
          {overview.data && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Until</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview.data.subscribers.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-muted-foreground">No active Deuxda Plus yet.</TableCell></TableRow>
                )}
                {overview.data.subscribers.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name || "—"}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>{s.months} months</TableCell>
                    <TableCell>{s.source === "paid" ? `${s.amount.toLocaleString("en-GB")} ${s.currency}` : "—"}</TableCell>
                    <TableCell>{s.source === "paid" ? "Paid" : "Granted"}</TableCell>
                    <TableCell>{new Date(s.expiresAt).toLocaleDateString("en-GB")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="space-y-6 p-6">
          <p className="text-sm font-semibold">Grant Deuxda Plus</p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">User ID</Label>
              <Input value={userId} onChange={(e) => setUserId(e.target.value.trim())} placeholder="00000000-0000-0000-0000-000000000000" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Period</Label>
              <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={months} onChange={(e) => setMonths(e.target.value)}>
                <option value="1">1 month</option>
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">12 months</option>
              </select>
            </div>
          </div>
          {userId && !validUser && <p className="text-sm text-destructive">Enter a valid user ID.</p>}
          <Button className="rounded-xl" disabled={!validUser || grant.isPending} onClick={() => grant.mutate()}>
            {grant.isPending ? "Granting…" : "Grant Deuxda Plus"}
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="space-y-5 p-6">
          <p className="text-sm font-semibold">New ad</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Image link (https)</Label>
              <Input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value.trim() })} placeholder="https://…" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Text</Label>
              <Input value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Destination link (optional, https)</Label>
              <Input value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value.trim() })} placeholder="https://…" />
            </div>
          </div>
          <Button className="rounded-xl" disabled={!adValid || createAd.isPending} onClick={() => createAd.mutate()}>
            {createAd.isPending ? "Saving…" : "Create ad"}
          </Button>

          <div className="space-y-2 pt-4">
            {ads.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {ads.data?.length === 0 && <p className="text-sm text-muted-foreground">No ads yet.</p>}
            {ads.data?.map((ad) => (
              <div key={ad.id} className="flex items-center justify-between rounded-xl bg-muted/40 p-3 text-sm">
                <span>
                  <span className="font-semibold">{ad.title}</span> · {new Date(ad.created_at).toLocaleDateString("en-GB")}
                </span>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={() => toggleAd.mutate({ id: ad.id, isActive: !ad.is_active })}>
                  {ad.is_active ? "Turn off" : "Turn on"}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
